import {
  useCreatePromptTemplateMutation,
  useUpdatePromptTemplateMutation,
} from '@saaga/shared/store/prompt-template-endpoints.store'
import { useCallback, useState } from 'react'

import type { PromptTemplate } from '@/components/chat/prompt-template.interface'

import { savePromptTemplate } from './prompt-template-crud.helper'
import type { PromptTemplateDrawerFormSchemaType } from './prompt-template-drawer-form.schema'
import { usePromptTemplateDeleteConfirm } from './use-prompt-template-delete-confirm.hook'

interface UsePromptTemplateCrudProps {
  agencySlug?: string
}

interface UsePromptTemplateCrudReturn {
  isDrawerOpen: boolean
  editingTemplate: PromptTemplate | null
  templateToDelete: PromptTemplate | null
  setIsDrawerOpen: (isOpen: boolean) => void
  handleEdit: (template: PromptTemplate) => void
  handleDeleteRequest: (template: PromptTemplate) => void
  handleDeleteConfirm: () => void
  handleDeleteCancel: () => void
  handleCreate: () => void
  handleSave: (values: PromptTemplateDrawerFormSchemaType) => void
}

export const usePromptTemplateCrud = ({
  agencySlug,
}: UsePromptTemplateCrudProps = {}): UsePromptTemplateCrudReturn => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null)
  const [createFn] = useCreatePromptTemplateMutation()
  const [updateFn] = useUpdatePromptTemplateMutation()
  const deleteConfirm = usePromptTemplateDeleteConfirm()

  const handleEdit = useCallback((template: PromptTemplate) => {
    setEditingTemplate(template)
    setIsDrawerOpen(true)
  }, [])

  const handleCreate = useCallback(() => {
    setEditingTemplate(null)
    setIsDrawerOpen(true)
  }, [])

  const handleSave = useCallback(
    async (values: PromptTemplateDrawerFormSchemaType) => {
      const isSaved = await savePromptTemplate({
        values,
        templateId: editingTemplate?.id ?? null,
        agencySlug,
        createFn,
        updateFn,
      })
      if (isSaved) {
        setIsDrawerOpen(false)
        setEditingTemplate(null)
      }
    },
    [editingTemplate, createFn, updateFn, agencySlug]
  )

  return {
    isDrawerOpen,
    editingTemplate,
    setIsDrawerOpen,
    handleEdit,
    handleCreate,
    handleSave,
    ...deleteConfirm,
  }
}
